import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Box, Instagram, MapPin, Menu, MessageCircle, Phone, Sparkles, User, Users, UsersRound, X, Zap } from "lucide-react";
import "@/App.css";
import { api, API, WHATSAPP, MAPS, INSTAGRAM } from "../lib/api";
import Reveal from "../components/landing/Reveal";
import PairsSection from "../components/landing/PairsSection";

const MARQUEE = ["Single statues", "Couples", "Families", "Pure PLA", "Hand-finished", "Made in Raipur"];
const EASE = [0.22, 1, 0.36, 1];
const PRODUCTS = [
  { id: "single", name: "Single", people: "1 person", icon: User, prices: { "10": 1000, "20": 2000 } },
  { id: "couple", name: "Couple", people: "2 people", icon: Users, prices: { "10": 1700, "20": 3400 } },
  { id: "family", name: "Family", people: "3-4 people", icon: UsersRound, prices: { "10": 3000, "20": 6000 } },
];

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [siteImages, setSiteImages] = useState({});
  const lenisRef = useRef(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroImgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const heroCopyY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    api.get("/site-images").then((r) => setSiteImages(r.data)).catch(() => {});
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenisRef.current) lenisRef.current.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div className="site-shell">
      <div className="noise" aria-hidden="true" />
      <div className="top-banner" data-testid="top-banner">
        <Phone size={13} className="cyan" />
        <span>Call or WhatsApp us:</span>
        <a href="tel:+919343920661" data-testid="top-banner-phone">+91 9343920661</a>
      </div>
      <nav className="nav" data-testid="main-navigation">
        <button className="brand" data-testid="brand-home-button" onClick={() => scrollTo("top")}><span className="brand-mark"><Box size={18} strokeWidth={2.5} /></span><span>Biswas<span className="cyan">3D</span></span></button>
        <div className={`nav-links ${menuOpen ? "is-open" : ""}`} data-testid="navigation-links">
          <button data-testid="nav-work-button" onClick={() => scrollTo("work")}>Our work</button>
          <button data-testid="nav-process-button" onClick={() => scrollTo("process")}>Process</button>
          <button data-testid="nav-pricing-button" onClick={() => scrollTo("pricing")}>Pricing</button>
          <button data-testid="nav-contact-button" onClick={() => scrollTo("contact")}>Contact</button>
        </div>
        <Link to="/order" className="nav-cta" data-testid="nav-order-button">Order now <ArrowUpRight size={16} /></Link>
        <button className="menu-button" data-testid="mobile-menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle navigation">{menuOpen ? <X /> : <Menu />}</button>
      </nav>

      <main id="top">
        <section className="hero section-wrap" ref={heroRef} data-testid="hero-section">
          <motion.div className="hero-copy" style={{ y: heroCopyY }}>
            <motion.p className="eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}><span className="eyebrow-dot" /> Raipur's human statue studio</motion.p>
            <h1>
              <span className="mask-line"><motion.span initial={{ y: "112%" }} animate={{ y: "0%" }} transition={{ duration: 0.95, delay: 0.22, ease: EASE }}>Your <em>people.</em></motion.span></span>
              <span className="mask-line"><motion.span initial={{ y: "112%" }} animate={{ y: "0%" }} transition={{ duration: 0.95, delay: 0.36, ease: EASE }}><span className="dim">Printed in 3D.</span></motion.span></span>
            </h1>
            <motion.p className="hero-lede" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.52 }}>Send us a photo — we sculpt, print and hand-finish a lifelike statue of the people you love. Pure PLA, made to order in Raipur.</motion.p>
            <motion.div className="hero-actions" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.64 }}>
              <Link to="/order" className="button button-primary" data-testid="hero-order-button"><span>Order your statue</span><ArrowUpRight size={18} /></Link>
              <a className="button button-ghost" data-testid="hero-whatsapp-link" href={WHATSAPP} target="_blank" rel="noreferrer"><MessageCircle size={16} /> WhatsApp us</a>
            </motion.div>
            <motion.div className="hero-meta" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.82 }}><span><Zap size={15} /> From &#8377;1,000</span><span><Sparkles size={15} /> 100% PLA</span></motion.div>
          </motion.div>
          <motion.div className="hero-visual" style={{ y: heroImgY }} initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, delay: 0.4, ease: EASE }} data-testid="hero-visual">
            <div className="orbit orbit-one" /><div className="orbit orbit-two" />
            {siteImages.hero ? (
              <img src={`${API}/files/${siteImages.hero}`} alt="A finished Biswas3D human statue" data-testid="hero-image" />
            ) : (
              <div className="img-placeholder" data-testid="hero-image-placeholder"><Box size={42} strokeWidth={1.5} /><span>Studio photo coming soon</span></div>
            )}
            <div className="image-caption"><span className="live-dot" /> Material <strong>PLA, always</strong></div>
            <div className="hero-index">10 <span>/</span> 20 cm</div>
          </motion.div>
        </section>

        <div className="marquee" data-testid="studio-ticker">
          <div className="marquee-track">
            {[0, 1].map((g) => (
              <div className="marquee-group" key={g} aria-hidden={g === 1}>
                {MARQUEE.map((w) => <span key={`${g}-${w}`}>{w} <b>&#10022;</b></span>)}
              </div>
            ))}
          </div>
        </div>

        <PairsSection />

        <section className="process-section" id="process" data-testid="process-section">
          <div className="section-wrap">
            <Reveal className="section-heading process-heading"><div><p className="eyebrow">How it works</p><h2>From photo<br /><span>to statue.</span></h2></div><div className="process-note"><span>01 — 03</span><p>No complicated process. Photos, a secure payment, and a statue at your door.</p></div></Reveal>
            <Reveal delay={0.1} className="process-grid">
              <div><span>01</span><h3>Upload &amp; pay</h3><p>Pick a size, share clear photos, and pay securely in under a minute.</p></div>
              <div><span>02</span><h3>We confirm &amp; sculpt</h3><p>We call you to confirm details, then sculpt and print your piece in PLA.</p></div>
              <div><span>03</span><h3>Meet your mini-you</h3><p>Your statue is hand-finished and delivered, ready to gift or keep forever.</p></div>
            </Reveal>
          </div>
        </section>

        <section className="pricing-section section-wrap" id="pricing" data-testid="pricing-section">
          <Reveal className="section-heading"><div><p className="eyebrow">Simple pricing</p><h2>Pick your people.<br /><span>Pick a size.</span></h2></div><p className="heading-note">Every statue is pure PLA and hand-finished. 20 cm pieces are double the 10 cm price.</p></Reveal>
          <Reveal className="pricing-grid" delay={0.1}>
            {PRODUCTS.map((p) => {
              const Icon = p.icon;
              return (
                <div className="product-card pricing-card" key={p.id} data-testid={`pricing-card-${p.id}`}>
                  <Icon size={22} />
                  <span className="pc-name">{p.name}</span>
                  <span className="pc-people">{p.people}</span>
                  <span className="pc-price">&#8377;{p.prices["10"].toLocaleString("en-IN")} <small>/ 10 cm</small></span>
                  <span className="pc-alt">&#8377;{p.prices["20"].toLocaleString("en-IN")} for 20 cm</span>
                </div>
              );
            })}
          </Reveal>
          <Reveal className="pricing-cta" delay={0.15}>
            <Link to="/order" className="button button-primary" data-testid="pricing-order-button"><span>Place your order — pay securely</span><ArrowUpRight size={18} /></Link>
          </Reveal>
        </section>

        <section className="contact-section section-wrap" id="contact" data-testid="contact-section">
          <Reveal className="contact-card">
            <div><p className="eyebrow">Let's make someone</p><h2>Someone worth<br /><em>immortalising?</em></h2><p className="contact-copy">A parent, a partner, a whole family — tell us who, and we'll make them beautifully permanent.</p></div>
            <div className="contact-actions"><a className="button button-primary" data-testid="contact-whatsapp-link" href={WHATSAPP} target="_blank" rel="noreferrer"><MessageCircle size={17} /> Message on WhatsApp</a><a className="button button-ghost" data-testid="contact-phone-link" href="tel:+919343920661"><Phone size={17} /> +91 93439 20661</a></div>
          </Reveal>
          <Reveal delay={0.1} className="location-row">
            <div><MapPin size={20} className="cyan" /><div><strong>Visit our studio</strong><span data-testid="business-address">Room No 413, Avinash Lifestyle Empressa, Kota, Raipur, Chhattisgarh 492001</span></div></div>
            <a className="map-link" data-testid="maps-location-link" href={MAPS} target="_blank" rel="noreferrer">Open in Google Maps <ArrowUpRight size={16} /></a>
          </Reveal>
        </section>
      </main>
      <footer className="footer section-wrap">
        <div className="brand"><span className="brand-mark"><Box size={18} /></span><span>Biswas<span className="cyan">3D</span></span></div>
        <span className="footer-copy">Human statues in PLA. Made in Raipur. &copy; 2026</span>
        <div className="footer-links">
          <Link to="/terms" data-testid="footer-terms-link">Terms &amp; Conditions</Link>
          <Link to="/owner" data-testid="footer-owner-link">Owner studio</Link>
          <a data-testid="footer-instagram-link" href={INSTAGRAM} target="_blank" rel="noreferrer"><Instagram size={18} /> Instagram</a>
        </div>
      </footer>

      <div className="sticky-order-bar" data-testid="sticky-order-bar">
        <Link to="/order" className="button button-primary" data-testid="sticky-order-button"><span>Place your order</span><ArrowUpRight size={17} /></Link>
      </div>
    </div>
  );
}
